import { memo, useMemo } from 'react';
import { Close } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { CloseButton, Content, FileName, IFrame, PreviewDialog, Root, TopBar } from './index.styles';
import { WordPreview } from './WordPreview';
import { XlsxPreview } from './XlsxPreview';
import { DocPreview } from './DocPreview';
interface Props {
  open: boolean;
  onClose: () => void;
  url: Blob | string;
  name: string;
  typeOfStorage?: string;
  fileType?: string;
}

const PreviewItemBase = ({ open, onClose, url, name, typeOfStorage, fileType }: Props) => {
  const { t } = useTranslation();
  const isGoogleDrive = typeOfStorage === 'Gdrive';

  const content = useMemo(() => {
    if (!open || !url) return null;

    if (isGoogleDrive && typeof url === 'string') {
      const driveUrl = url.includes('/preview') ? url : `${url}/preview?usp=drive_web`;
      return <IFrame src={driveUrl} title={name} allow="autoplay; fullscreen" />;
    }

    const blobData = url as Blob;
    const ext = fileType?.toLowerCase() ?? '';

    switch (ext) {
      case 'docx':
      case 'dotx':
        return <WordPreview fileBlob={blobData} />;

      case 'xlsx':
      case 'xls':
      case 'csv':
      case 'xltx':
      case 'ods':
        return <XlsxPreview fileBlob={blobData} />;

      default:
        return <DocPreview fileBlob={url} fileName={name} fileType={ext} />;
    }
  }, [open, url, name, fileType, isGoogleDrive]);

  return (
    <PreviewDialog open={open} onClose={onClose} fullScreen>
      <Root>
        <TopBar>
          <FileName title={name}>{name}</FileName>
          <CloseButton onClick={onClose} aria-label={t('common.close')}>
            <Close />
          </CloseButton>
        </TopBar>
        <Content>{content}</Content>
      </Root>
    </PreviewDialog>
  );
};

export const PreviewItem = memo(PreviewItemBase) as typeof PreviewItemBase;
